import React, { useCallback, useMemo, useState } from 'react';
import styles from './superAdminHeLinks.module.scss';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import Button from '../../../components/button/Button';
import Search from '../../../components/search/Search';
import Table, { BodyColumn, HeaderColumn } from '../../../components/table/Table';
import { HeToken } from '../../../types';
import { useClientPagination } from '../../../hooks/useClientPagination';
import Spinner from '../../../components/spinner/Spinner';
import Pagination from '../../../components/pagination/Pagination';
import IconPencil from '../../../assets/icons/pencil.svg?react';
import CopyIcon from '../../../assets/icons/copy.svg?react';
import IconRotating from '../../../assets/icons/rotating_arrow.svg?react';
import { CopyToClipboard } from '@src/utils/componentCasts';
import { useToast } from 'src/hooks/useToast';
import { useHeTokensQuery } from '@api/graphql-codegen';
import { formatDate } from '@src/utils/time';
import AddModal from './add-modal/AddModal';
import FooterPage from '@src/components/footer-page/FooterPage';

type Props = {};

function SuperAdminHeLinks(props: Props) {
  const { data, loading, refetch } = useHeTokensQuery({
    notifyOnNetworkStatusChange: true,
  });
  const [perPage] = useState(50);
  const [search, setSearch] = useState('');
  const [selectedToken, setSelectedToken] = useState<HeToken>();
  const { successToast } = useToast();

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  function filterTokens(search: string) {
    const s = search.toLowerCase();
    return (c: HeToken) => {
      if (c.association && c.association.toLowerCase().includes(s)) return true;
      if (c.firstName && c.firstName.toLowerCase().includes(s)) return true;
      if (c.lastName && c.lastName.toLowerCase().includes(s)) return true;
      if (c.email && c.email.toLowerCase().includes(s)) return true;
      if (c.notes && c.notes.toLowerCase().includes(s)) return true;
      return false;
    };
  }

  const filter = useCallback((arr) => {
    return [...(arr || []).filter(filterTokens(search))];
  }, [search]);

  const { dataSlice: filteredData, onChangePage, totalFilteredItems, currentPage } = useClientPagination({
    data: data?.heTokens || [],
    perPage,
    onFilter: filter,
  });

  const [showAddModal, setShowAddModal] = useState(false);

  const handleChangeSearch = useCallback((v) => {
    setSearch(v);
  }, [setSearch]);

  const handleChangePage = (page: number) => {
    onChangePage(page);
  };

  const handleAdd = useCallback(() => {
    setSelectedToken(undefined);
    setShowAddModal(true);
  }, []);

  const handleCloseModal = () => {
    setSelectedToken(undefined);
    setShowAddModal(false);
  };

  const handleEdit = useCallback((token: HeToken) => () => {
    setSelectedToken(token);
    setShowAddModal(true);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearch('');
  }, []);

  const handleCopyToClipboard = useCallback((text: string) => {
    successToast(text);
  }, [successToast]);

  const headerColumns: HeaderColumn[] = useMemo(() => {
    return [
      {
        title: 'Association', sort: {
          key: 'association',
          asc: (a: HeToken, b: HeToken) => (a.association ?? '').localeCompare(b.association ?? ''),
          desc: (a: HeToken, b: HeToken) => (b.association ?? '').localeCompare(a.association ?? '')
        }
      }, {
        title: 'First Name', sort: {
          key: 'firstName',
          asc: (a: HeToken, b: HeToken) => (a.firstName ?? '').localeCompare(b.firstName ?? ''),
          desc: (a: HeToken, b: HeToken) => (b.firstName ?? '').localeCompare(a.firstName ?? '')
        }
      }, {
        title: 'Last Name', sort: {
          key: 'lastName',
          asc: (a: HeToken, b: HeToken) => (a.lastName ?? '').localeCompare(b.lastName ?? ''),
          desc: (a: HeToken, b: HeToken) => (b.lastName ?? '').localeCompare(a.lastName ?? '')
        }
      }, {
        title: 'Email', sort: {
          key: 'email',
          asc: (a: HeToken, b: HeToken) => (a.email ?? '').localeCompare(b.email ?? ''),
          desc: (a: HeToken, b: HeToken) => (b.email ?? '').localeCompare(a.email ?? '')
        }
      }, {
        title: 'Link', sort: {
          key: 'token',
          asc: (a: HeToken, b: HeToken) => a.token.localeCompare(b.token),
          desc: (a: HeToken, b: HeToken) => b.token.localeCompare(a.token)
        }
      }, {
        title: 'Signups', sort: {
          key: 'signups',
          asc: (a: HeToken, b: HeToken) => (a.signups ?? 0) - (b.signups ?? 0),
          desc: (a: HeToken, b: HeToken) => (b.signups ?? 0) - (a.signups ?? 0)
        }
      }, {
        title: 'Link Date', sort: {
          key: 'generatedAt',
          asc: (a: HeToken, b: HeToken) => (a.generatedAt ?? '').localeCompare(b.generatedAt ?? ''),
          desc: (a: HeToken, b: HeToken) => (b.generatedAt ?? '').localeCompare(a.generatedAt ?? '')
        }
      }, {
        title: ''
      }
    ];
  }, []);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    return [
      {
        key: (row: HeToken) => {
          if (!(row?.association)) {
            return <div style={{color: "red"}}> - </div>
          }
          return <div>{row.association}</div>
        }, className: styles.firLas
      }, {
        key: (row: HeToken) => {
          if (!(row?.firstName)) {
            return <div style={{color: "red"}}> - </div>
          }
          return <div>{row.firstName}</div>
        }, className: styles.firLas
      }, {
        key: (row: HeToken) => {
          if (!(row?.lastName)) {
            return <div style={{color: "red"}}> - </div>
          }
          return <div>{row.lastName}</div>
        }, className: styles.firLas
      }, {
        key: (row: HeToken) => {
          if (!(row?.email)) {
            return <div style={{color: "red"}}> - </div>
          }
          return <div>{row.email}</div>
        }, className: styles.columnAccSchEma
      }, {
        key: (row: HeToken) => {
          const domain = window.location.host.includes('openparachuteschools') ? 'https://home.openparachuteschools.com' : 'https://he.openparachuteapp.com'
          return <CopyToClipboard
                    text={`${domain}/registration?token=${row.token}`}
                    onCopy={() => handleCopyToClipboard('Link copied!')}>
                    <div className={styles.iconWrapperCopy}>
                      <span style={{cursor: "pointer"}}>
                        {`...com/registration?token=${row.token}`}
                      </span>
                      <CopyIcon width="20px" height="20px" className={styles.iconWrapper}/>
                    </div>
                 </CopyToClipboard>
        }, className: styles.columnAccSchEma
      }, {
        key: (row: HeToken) => {
          return <div>{row.signups ?? 0}</div>
        }, className: styles.columnAccSchEma
      }, {
        key: (row: HeToken) => {
          if (!(row?.generatedAt)) {
            return <div style={{color: "red"}}> - </div>
          }
          return <div>{formatDate(row.generatedAt)}</div>
        }, className: styles.columnAccSchEma
      }, {
        key: (row: HeToken) => {
          return (
            <div style={{position: "relative"}}>
              <div className={styles.actionWrapper} onClick={handleEdit(row)}>
                <IconPencil className={styles.iconWrapper}/>
              </div>
            </div>
          )
        },
      }
    ];
  }, [handleEdit, handleCopyToClipboard]);

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.header}>
          <h2 className={styles.title}>Home Ed Links</h2>
          <div>
            <Button onClick={handleAdd} className={styles.generateBtn} label={'Generate Link'}/>
          </div>
        </div>
        <div className={styles.search}>
          <Search onChange={handleChangeSearch} value={search}/>
          <div className={styles.filters}>
            <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
              size={'extraSmall'} outline={true} color={'green'}
              leftIcon={<IconRotating className={styles.resetIcon}/>}/>
          </div>
        </div>
        <div className={loading ? styles.content : ""}>
          {loading ? <Spinner/> : <>
            <Table color={'green'} header={headerColumns} body={bodyColumns} data={filteredData} noItemsText="No active links." />
            {data?.heTokens && <div className={styles.paginationWrapper}>
              <Pagination currentPage={currentPage} perPage={perPage} totalItems={totalFilteredItems} onPageChange={handleChangePage}/>
            </div>}
          </>}
        </div>
      </div>
      <FooterPage />
      {/* <Footer/> */}
      <AddModal onCompleted={handleRefetch} open={showAddModal} onClose={handleCloseModal} token={selectedToken}/>
    </>
  );
}

export default SuperAdminHeLinks;
